import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useBulkImport } from '@/contexts/BulkImportContext';
import { CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META: FeatureMeta = {
  id: 'bulk-import-banner',
  label: 'Bulk Import Progress Banner',
  description: 'Persistent banner showing live progress of the active bulk import (Excel or email ingestion) plus a post-completion processing-issues summary grouped by severity.',
  triggerType: 'panel',
  personas: ['seller', 'buyer_seller'],
};

export default function BulkImportBanner() {
  useFeatureRegistration(FEATURE_META);
  const { showBanner, progress, isError, dismissBanner } = useBulkImport();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  if (!showBanner || !progress) return null;

  const listingsCreated = progress.listings_created ?? 0;
  const listingsUpdated = progress.listings_updated ?? 0;
  const hasListings = listingsCreated > 0 || listingsUpdated > 0;

  const handleViewListings = () => {
    queryClient.invalidateQueries({ queryKey: ['listings'] });
    queryClient.invalidateQueries({ queryKey: ['menu-listings'] });
    dismissBanner();
    navigate('/my-listings');
  };

  // Build summary text
  let summaryText: string;
  if (isError) {
    summaryText = progress.processing_error || 'Bulk import failed. Please try again.';
  } else {
    const parts: string[] = [];
    if (listingsCreated > 0) parts.push(`${listingsCreated} listing${listingsCreated !== 1 ? 's' : ''} created`);
    if (listingsUpdated > 0) parts.push(`${listingsUpdated} listing${listingsUpdated !== 1 ? 's' : ''} updated`);
    summaryText = `Bulk import complete: ${parts.join(', ') || 'No listings imported'}`;
  }

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[60] transition-transform duration-300 ease-out ${
        isError
          ? 'bg-destructive text-destructive-foreground'
          : 'bg-emerald-600 text-white'
      }`}
      style={{ transform: 'translateY(0)' }}
    >
      <div className="container-wide py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {isError ? (
            <AlertTriangle className="w-5 h-5 shrink-0" />
          ) : (
            <CheckCircle2 className="w-5 h-5 shrink-0" />
          )}
          <p className="text-sm font-medium truncate">{summaryText}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {!isError && hasListings && (
            <Button
              variant="secondary"
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={handleViewListings}
            >
              View Listings
            </Button>
          )}
          <Button variant="quiet" size="icon-sm" type="button"
            onClick={dismissBanner}
            className="shrink-0 p-1 rounded-md hover:bg-white/20 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
